<template>
  <div class="page-container">
    <MainTitle
      title="Chat Session Insights"
      message=""
    />
    <div class="app-content-container">
      <div class="summary-wrapper">
        <!-- Header -->
        <div class="summary-header">
          <Button
            text
            icon="pi pi-arrow-left"
            label="Back to History"
            class="back-btn"
            @click="goToHistory"
          />
          <h1 class="summary-title">
            Session Insights
          </h1>
          <FeedbackThumbs
            v-if="status === 'loaded' && insights.insights_id"
            class="header-feedback"
            :feature-type="'chat_insights'"
            :target-id="insights.insights_id"
          />
        </div>

        <!-- Loading state -->
        <div
          v-if="status === 'loading'"
          class="status-container"
        >
          <ProgressSpinner />
          <p class="status-text">
            {{ INSIGHTS_LOADING_MSG }}
          </p>
        </div>

        <!-- Error state -->
        <div
          v-else-if="status === 'error'"
          class="status-container"
        >
          <i class="pi pi-exclamation-triangle status-icon error-icon" />
          <p class="status-text">
            {{ errorMessage }}
          </p>
          <div class="action-buttons">
            <Button
              label="Retry"
              icon="pi pi-refresh"
              @click="loadInsights"
            />
            <Button
              text
              label="Back to Hub"
              icon="pi pi-home"
              @click="goToHub"
            />
          </div>
        </div>

        <!-- Loaded state -->
        <div
          v-else-if="status === 'loaded'"
          class="insights-sections"
        >
          <InsightsSummary
            :summary-learner-language="insights.summary_learner_language"
            :summary-target-language="insights.summary_target_language"
          />
          <InsightsFeedback :feedback="insights.feedback" />
          <InsightsVocabulary 
            :items="insights.vocabulary_and_grammar" 
            :subject="subject" 
          />
          <InsightsResources 
            :resources="insights.resources"
            :subject="subject" 
          />

          <div class="action-buttons">
            <Button
              label="Try Another Scenario"
              icon="pi pi-play"
              @click="startNewChat"
            />
            <Button
              text
              label="Back to Hub"
              icon="pi pi-home"
              @click="goToHub"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import InsightsSummary from './insights/InsightsSummary.vue'
import InsightsFeedback from './insights/InsightsFeedback.vue'
import InsightsVocabulary from './insights/InsightsVocabulary.vue'
import InsightsResources from './insights/InsightsResources.vue'
import FeedbackThumbs from '@/components/feedback/FeedbackThumbs.vue'
import MainTitle from '@/components/title/MainTitle.vue'
import { getInsights } from '@/services/chatInsightsService'
import {
  INSIGHTS_LOADING_MSG,
  INSIGHTS_LOAD_ERROR_MSG,
  INSIGHTS_STILL_ACTIVE_MSG
} from '@/assets/constants/constants'

export default {
  name: 'ChatSummary',
  components: { 
    Button, 
    ProgressSpinner, 
    InsightsSummary, 
    InsightsFeedback, 
    InsightsVocabulary, 
    InsightsResources,
    FeedbackThumbs,
    MainTitle
  },
  props: {
    sessionId: {
      type: String,
      required: true
    },
    subject: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      status: 'loading',
      insights: null,
      errorMessage: '',
      INSIGHTS_LOADING_MSG
    }
  },
  mounted() {
    this.loadInsights()
  },
  methods: {
    async loadInsights() {
      this.status = 'loading'
      this.errorMessage = ''
      try {
        this.insights = await getInsights(this.sessionId)
        this.status = 'loaded'
      } catch (error) {
        const statusCode = error.response?.status
        if (statusCode === 400) {
          this.errorMessage = INSIGHTS_STILL_ACTIVE_MSG
        } else if (statusCode === 401 || statusCode === 403) {
          this.$router.push({ name: 'LoginForm' })
          return
        } else {
          this.errorMessage = INSIGHTS_LOAD_ERROR_MSG
        }
        this.status = 'error'
      }
    },
    startNewChat() {
      this.$router.push({ name: 'ChatSubjectInput' })
    },
    goToHistory() {
      this.$router.push({ name: 'ChatHistory' })
    },
    goToHub() {
      this.$router.push({ name: 'ChatHub' })
    }
  }
}
</script>

<style scoped>
.summary-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 760px;
  padding: 24px 0 48px;
  margin: 0 auto;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-bottom: 32px;
}

.header-feedback {
  margin-left: auto;
}

.back-btn {
  padding-left: 0 !important;
}

.summary-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
}

.status-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 60px 0;
}

.status-text {
  color: var(--text-color-secondary, #6b7280);
  font-size: 0.95rem;
  margin: 0;
}

.status-icon {
  font-size: 2.5rem;
}

.error-icon {
  color: #dc2626;
}

.insights-sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
  padding-top: 8px;
}
</style>

